import {
	createEmptyContactDraft,
	type ContactDraft,
} from '#app/routes/mocra+/_form+/components/contacts/types.ts'
import {
	contactDraftToReference,
	ContactReferenceSchema,
	type ContactReference,
	normalizeContactReference,
} from '#app/routes/mocra+/_form+/contacts/reference.ts'
import { assertEntityDefinition } from '#app/routes/mocra+/_form+/entity-definition.ts'

type ContactReferenceDefinitionOptions = {
	readonly defaultTitle: string
}

export function createContactReferenceDefinition(
	options: ContactReferenceDefinitionOptions,
) {
	const { defaultTitle } = options
	return assertEntityDefinition<ContactReference, ContactDraft>({
		schema: ContactReferenceSchema,
		createDraft: () => createEmptyContactDraft(),
		sanitizeDraft: (draft) =>
			sanitizeContactReferenceDraft(draft, defaultTitle),
		serialize: (draft) => serializeDraft(draft, defaultTitle),
		deserialize: (entry) => deserializeEntry(entry, defaultTitle),
		collectErrors: (draft) => collectDraftErrors(draft),
	})
}

export function sanitizeContactReferenceDraft(
	draft: ContactDraft,
	defaultTitle: string,
): ContactDraft {
	return {
		id: draft.id?.trim(),
		title: draft.title?.trim() || defaultTitle,
		name: draft.name.trim(),
		email: draft.email.trim(),
		phone: draft.phone?.trim() ?? '',
	}
}

function serializeDraft(draft: ContactDraft, defaultTitle: string) {
	if (!draft.id || draft.id.trim().length === 0) {
		throw new Error('Contact references require a stable contact id.')
	}
	return contactDraftToReference(
		sanitizeContactReferenceDraft(draft, defaultTitle),
		{
			defaultTitle,
		},
	)
}

function deserializeEntry(
	entry: ContactReference,
	defaultTitle: string,
): ContactDraft {
	const normalized = normalizeContactReference(entry)
	return {
		id: normalized.contactId,
		title: normalized.fallbackTitle ?? defaultTitle,
		name: normalized.fallbackName ?? '',
		email: normalized.fallbackEmail ?? '',
		phone: normalized.fallbackPhone ?? '',
	}
}

function collectDraftErrors(draft: ContactDraft): readonly string[] {
	const messages = new Set<string>()
	if (!draft.id || draft.id.trim().length === 0) {
		messages.add('Select a contact from the directory.')
	}
	if (!draft.email || draft.email.trim().length === 0) {
		messages.add('Enter the contact email.')
	}
	return [...messages]
}
